import { 
  collection, 
  getDocs, 
  doc,
  getDoc,
  query, 
  orderBy 
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Payment } from '../types';

export const paymentsService = {
  // Get all payments for a specific occasion
  async getPaymentsByOccasionId(occasionId: string): Promise<Payment[]> {
    try {
      // First try to get payments from subcollection
      try {
        const paymentsRef = collection(db, 'Occasions', occasionId, 'payments');
        const q = query(paymentsRef, orderBy('paymentDate', 'desc'));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          return querySnapshot.docs.map(doc => ({
            paymentId: doc.id,
            ...doc.data()
          } as Payment));
        }
      } catch (subcollectionError) {
        console.log('No subcollection found, checking for payments array in occasion document');
      }

      // If no subcollection, check if payments are stored as an array in the occasion document
      const occasionRef = doc(db, 'Occasions', occasionId);
      const occasionDoc = await getDoc(occasionRef);
      
      if (occasionDoc.exists()) {
        const occasionData = occasionDoc.data();
        
        // Check for different possible payment field names
        const paymentsArray = occasionData.payments || 
                             occasionData.payment || 
                             occasionData.paymentList || 
                             [];
        
        if (Array.isArray(paymentsArray) && paymentsArray.length > 0) {
          return paymentsArray.map((payment, index) => ({
            paymentId: payment.paymentId || payment.id || `payment_${index}`,
            ...payment
          } as Payment));
        }
      }
      
      return [];
    } catch (error) {
      console.error('Error fetching payments:', error);
      throw error;
    }
  },

  // Get all payments across all occasions
  async getAllPayments(): Promise<Payment[]> {
    try {
      // First get all occasions
      const occasionsRef = collection(db, 'Occasions');
      const occasionsSnapshot = await getDocs(occasionsRef);
      
      const allPayments: Payment[] = [];
      
      // For each occasion, get its payments
      for (const occasionDoc of occasionsSnapshot.docs) {
        try {
          const payments = await this.getPaymentsByOccasionId(occasionDoc.id);
          allPayments.push(...payments);
        } catch (error) {
          console.error(`Error fetching payments for occasion ${occasionDoc.id}:`, error);
        }
      }
      
      // Sort by payment date
      return allPayments.sort((a, b) => 
        new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime()
      );
    } catch (error) {
      console.error('Error fetching all payments:', error);
      throw error;
    }
  }
};
